import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/auth";

import Landing from "@/pages/Landing";
import CommentCaMarche from "@/pages/CommentCaMarche";
import BilanSuccession from "@/pages/BilanSuccession";
import BilanDirigeants from "@/pages/BilanDirigeants";
import OuvrirSuccession from "@/pages/OuvrirSuccession";
import { SuccessionPublicWizard, SuccessionPublicResult } from "@/pages/SuccessionPublicWizard";
import Succession from "@/pages/Succession";
import FoncierClair from "@/pages/FoncierClair";
import ChoixEspace from "@/pages/ChoixEspace";
import Services from "@/pages/Services";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Pricing from "@/pages/Pricing";
import PaymentSuccess from "@/pages/PaymentSuccess";
import AdminLogin from "@/pages/AdminLogin";
import AdminTotpReset from "@/pages/AdminTotpReset";
import ProsLanding from "@/pages/ProsLanding";
import Parrainage from "@/pages/Parrainage";
import Contact from "@/pages/Contact";

import DashboardLayout from "@/pages/DashboardLayout";
import CarnetSection from "@/pages/CarnetSection";
import Synthese from "@/pages/Synthese";
import Coffre from "@/pages/Coffre";
import Partages from "@/pages/Partages";
import Securite from "@/pages/Securite";
import Subscription from "@/pages/Subscription";
import Partenaire from "@/pages/Partenaire";
import Admin from "@/pages/Admin";
import AdminPartenaires from "@/pages/AdminPartenaires";
import AdminHeritier from "@/pages/AdminHeritier";
import AdminConversions from "@/pages/AdminConversions";
import Annuaire from "@/pages/Annuaire";
import AccesHeritier from "@/pages/AccesHeritier";
import HeritierView from "@/pages/HeritierView";
import { CoupleAccept, CarnetConjoint } from "@/pages/Couple";
import LegalPage from "@/pages/LegalPage";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import CookieConsent from "@/components/CookieConsent";

function ProtectedRoute({ children, role, unauthRedirect }) {
  const { user } = useAuth();
  if (user === null) {
    return <div className="min-h-screen flex items-center justify-center">Chargement…</div>;
  }
  if (user === false) {
    if (unauthRedirect) return <Navigate to={unauthRedirect} replace />;
    // Admin routes redirect to dedicated admin login
    return <Navigate to={role === "admin" ? "/direction/connexion" : "/connexion"} replace />;
  }
  if (role && user.role !== role && user.role !== "admin") {
    return <Navigate to="/carnet/identite" replace />;
  }
  return children;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/comment-ca-marche" element={<CommentCaMarche />} />
          <Route path="/bilan-succession" element={<BilanSuccession />} />
          <Route path="/bilan-dirigeants" element={<BilanDirigeants />} />
          {/* Page de communication autonome — landing pour campagnes/pubs/réseaux sociaux */}
          <Route path="/ouvrir-une-succession" element={<OuvrirSuccession />} />
          {/* Tunnel public de diagnostic — sans compte, aligné sur /bilan-succession */}
          <Route path="/ouvrir-une-succession/diagnostic" element={<SuccessionPublicWizard />} />
          <Route path="/ouvrir-une-succession/resultat" element={<SuccessionPublicResult />} />
          <Route path="/services" element={<Services />} />
          <Route path="/connexion" element={<Login />} />
          <Route path="/inscription" element={<Register />} />
          <Route path="/mot-de-passe-oublie" element={<ForgotPassword />} />
          <Route path="/mot-de-passe-reinitialiser" element={<ResetPassword />} />
          <Route path="/tarifs" element={<Pricing />} />
          <Route path="/pros" element={<ProsLanding />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/paiement/succes" element={<PaymentSuccess />} />

          {/* FONCIER CLAIR — outil privé AUPRÉA, URL non-devinable via env */}
          {process.env.REACT_APP_FC_SLUG && (
            <Route path={`/consultation-privee-${process.env.REACT_APP_FC_SLUG}`} element={<FoncierClair />} />
          )}

          {/* Espaces client — sélection et parcours succession */}
          <Route path="/choix-espace" element={<ProtectedRoute><ChoixEspace /></ProtectedRoute>} />
          <Route path="/succession" element={<ProtectedRoute unauthRedirect="/ouvrir-une-succession"><Succession /></ProtectedRoute>} />

          {/* Admin (espace direction) — URL discrète */}
          <Route path="/direction" element={<Navigate to="/direction/connexion" replace />} />
          <Route path="/direction/connexion" element={<AdminLogin />} />
          <Route path="/direction/2fa-reset" element={<AdminTotpReset />} />

          {/* Pages légales (RGPD / mentions / CGU / CGV) */}
          <Route path="/legal" element={<Navigate to="/legal/mentions-legales" replace />} />
          <Route path="/legal/:slug" element={<LegalPage />} />
          <Route path="/mentions-legales" element={<Navigate to="/legal/mentions-legales" replace />} />
          <Route path="/cgu" element={<Navigate to="/legal/cgu" replace />} />
          <Route path="/cgv" element={<Navigate to="/legal/cgv" replace />} />
          <Route path="/politique-confidentialite" element={<Navigate to="/legal/confidentialite" replace />} />

          {/* Mode héritier — accès public (sans auth) */}
          <Route path="/acces-heritier" element={<AccesHeritier />} />
          <Route path="/heritier/:token" element={<HeritierView />} />
          <Route path="/couple/accepter" element={<CoupleAccept />} />

          <Route element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
            <Route path="/carnet" element={<Navigate to="/carnet/identite" replace />} />
            <Route path="/carnet/:rubriqueId" element={<CarnetSection />} />
            <Route path="/synthese" element={<Synthese />} />
            <Route path="/coffre" element={<Coffre />} />
            <Route path="/partages" element={<Partages />} />
            <Route path="/securite" element={<Securite />} />
            <Route path="/abonnement" element={<Subscription />} />
            <Route path="/parrainage" element={<Parrainage />} />
            <Route path="/annuaire" element={<Annuaire />} />
            <Route path="/conjoint" element={<CarnetConjoint />} />
            <Route path="/partenaire" element={<ProtectedRoute role="partner"><Partenaire /></ProtectedRoute>} />
            <Route path="/admin" element={<ProtectedRoute role="admin"><Admin /></ProtectedRoute>} />
            <Route path="/admin/partenaires" element={<ProtectedRoute role="admin"><AdminPartenaires /></ProtectedRoute>} />
            <Route path="/admin/heritier" element={<ProtectedRoute role="admin"><AdminHeritier /></ProtectedRoute>} />
            <Route path="/admin/conversions" element={<ProtectedRoute role="admin"><AdminConversions /></ProtectedRoute>} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <CookieConsent />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
